import { Global, css } from "@emotion/react";
import { BasicModal, Box, Theme } from "@outschool/backpack";
import { useTranslation } from "@outschool/localization";
import {
  OsUppyFile,
  AllowedSources as UppyAllowedSources,
  UppyComponent,
  UppyType,
} from "@outschool/ui-uppy";
import React from "react";

import { reformatUppyFile } from "../lib/util";

const ALLOWED_FILE_TYPES = {
  // keep in sync with lang-js/apps/media/src/services/imageProcessor.ts
  images: [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/webp",
    "image/gif",
    "image/tiff",
    "image/bmp",
    "image/heic",
    "image/heif",
    "image/svg+xml",
    "image/NEF",
    "image/x-adobe-dng",
  ],
  // top 9 video formats in our db
  // after these, the number of videos for other formats are in double digits
  videos: [
    "video/mp4",
    "video/quicktime",
    "video/webm",
    "video/x-ms-wmv",
    "video/x-matroska",
    "video/mpeg",
    "video/avi",
    "video/x-m4v",
    "video/3gpp",
  ],
  others: ["*/*"],
};

export function ImageUpload({
  renderButton,
  cropRatio,
  onChange,
  maxSize = 5 * 1024 * 1024,
  onFileAdded = async () => {},
  uppyAllowedSources = "all",
}: {
  renderButton: (onPick: () => void) => React.ReactNode;
  cropRatio?: number;
  onChange: (file: UploadedFile) => void;
  maxSize?: number;
  uppyAllowedSources?: UppyAllowedSources;
  onFileAdded?: (file: OsUppyFile) => void;
}) {
  const handleUploaded = React.useCallback(
    (files: UploadedFile[]) => {
      onChange(files[0]);
    },
    [onChange],
  );
  return (
    <FileUploadModal
      renderButton={renderButton}
      cropRatio={cropRatio}
      uppyAllowedSources={uppyAllowedSources}
      maxSize={maxSize}
      maxFiles={1}
      allowedFileTypes={ALLOWED_FILE_TYPES.images}
      onUploaded={handleUploaded}
      onFileAdded={onFileAdded}
      onFilesUploaded={(files: OsUppyFile[]) => {
        const converted = files.map((file: OsUppyFile) =>
          reformatUppyFile({
            uppyFile: file,
            source: file.source ?? "local_storage",
          }),
        );
        return handleUploaded(converted);
      }}
    />
  );
}

export function PrivateAttachmentUpload({
  renderButton,
  onFilesUploaded,
  allowedFileTypes,
  maxFiles = 5,
  uppyAllowedSources = "filesystem",
  cropRatio,
  maxSize = 50 * 1024 * 1024,
}: {
  renderButton: (onPick: () => void) => React.ReactNode;
  onFilesUploaded: (files: OsUppyFile[]) => void;
  allowedFileTypes: string[];
  maxFiles?: number;
  uppyAllowedSources?: UppyAllowedSources;
  cropRatio?: number;
  maxSize?: number;
}) {
  return (
    <FileUploadModal
      renderButton={renderButton}
      uppyAllowedSources={uppyAllowedSources}
      maxSize={maxSize}
      maxFiles={maxFiles}
      onFilesUploaded={onFilesUploaded}
      usePrivateUpload={true}
      allowedFileTypes={allowedFileTypes}
      onFileAdded={() => {}}
      cropRatio={cropRatio}
    />
  );
}

interface AttachmentUploadBaseProps {
  onUploaded: (files: UploadedFile[]) => void;
  maxFiles?: number;
  uppyAllowedSources?: UppyAllowedSources;
  cropRatio?: number;
  maxSize?: number;
  onFileAdded?: (file: OsUppyFile) => void;
}

interface AttachmentUploadWithButtonProps extends AttachmentUploadBaseProps {
  renderButton: (onPick: () => void) => React.ReactNode;
  isOpen?: never;
  setIsOpen?: never;
}

interface AttachmentUploadWithoutButtonProps extends AttachmentUploadBaseProps {
  renderButton?: never;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

export function AttachmentUpload({
  onUploaded = () => {},
  maxFiles = 5,
  uppyAllowedSources = "all",
  cropRatio,
  maxSize = 50 * 1024 * 1024,
  renderButton,
  isOpen,
  setIsOpen,
  onFileAdded,
}: AttachmentUploadWithButtonProps | AttachmentUploadWithoutButtonProps) {
  return (
    <FileUploadModal
      uppyAllowedSources={uppyAllowedSources}
      maxSize={maxSize}
      maxFiles={maxFiles}
      onUploaded={onUploaded}
      cropRatio={cropRatio}
      allowedFileTypes={ALLOWED_FILE_TYPES.others}
      onFileAdded={onFileAdded}
      onFilesUploaded={(files: OsUppyFile[]) => {
        const converted = files.map((file: OsUppyFile) =>
          reformatUppyFile({
            uppyFile: file,
            source: file.source ?? "local_storage",
          }),
        );
        return onUploaded(converted);
      }}
      {...(renderButton !== undefined
        ? {
            renderButton: renderButton!,
          }
        : {
            isOpen: isOpen,
            onClose: () => setIsOpen(false),
          })}
    />
  );
}

interface VideoUploadBaseProps {
  onUploaded: (file: UploadedFile) => void;
  onFileAdded?: (file: OsUppyFile) => void;
  uppyAllowedSources?: UppyAllowedSources;
  onError?: (e: string) => void;
}

interface VideoUploadWithButtonProps extends VideoUploadBaseProps {
  renderButton: (onPick: () => void) => React.ReactNode;
  isOpen?: never;
  setIsOpen?: never;
}

interface VideoUploadWithoutButtonProps extends VideoUploadBaseProps {
  renderButton?: never;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

export function VideoUpload({
  renderButton,
  onUploaded,
  onFileAdded,
  uppyAllowedSources,
  onError = e => console.log(e),
  isOpen,
  setIsOpen,
}: VideoUploadWithButtonProps | VideoUploadWithoutButtonProps) {
  const { t } = useTranslation("ui-components-classroom\\FilestackUpload");
  const handleUploaded = React.useCallback(
    (files: UploadedFile[]) => {
      const file = files[0];
      if (file.size === 0) {
        onError(t("File size is zero"));
      } else {
        onUploaded(file);
      }
    },
    [onUploaded, onError, t],
  );

  return (
    <FileUploadModal
      uppyAllowedSources={uppyAllowedSources}
      maxSize={1000 * 1024 * 1024}
      maxFiles={1}
      allowedFileTypes={ALLOWED_FILE_TYPES.videos}
      onUploaded={handleUploaded}
      onFileAdded={onFileAdded}
      onFilesUploaded={(files: OsUppyFile[]) => {
        if (!files || !files[0]) {
          return;
        }
        return onUploaded(
          reformatUppyFile({
            uppyFile: files[0],
            source: files[0].source ?? "local_storage",
          }),
        );
      }}
      {...(renderButton !== undefined
        ? {
            renderButton: renderButton!,
          }
        : {
            isOpen: isOpen,
            onClose: () => setIsOpen(false),
          })}
    />
  );
}

export interface UploadedFile {
  url: string;
  size: number;
  source: string;
  status: string;
  filename: string;
  mimetype: string;
  originalPath: string;
  key: string;
}

interface FileUploadModalBaseProps {
  onUploaded?: (files: UploadedFile[]) => void;
  onFileAdded?: (file: OsUppyFile, uppy: UppyType) => void;
  onFilesUploaded?: (files: OsUppyFile[]) => void;
  usePrivateUpload?: boolean;
  allowedFileTypes: string[];
  cropRatio?: number;
  uppyAllowedSources?: UppyAllowedSources;
  maxSize: number;
  maxFiles: number;
}

interface FileUploadModalWithButtonProps extends FileUploadModalBaseProps {
  renderButton: (openModal: () => void) => React.ReactNode;
  isOpen?: never;
  onClose?: never;
}

interface FileUploadModalWithoutButtonProps extends FileUploadModalBaseProps {
  renderButton?: never;
  isOpen: boolean;
  onClose: () => void;
}

function FileUploadModal({
  onUploaded,
  onFileAdded,
  onFilesUploaded,
  cropRatio,
  uppyAllowedSources = "all",
  maxSize,
  maxFiles,
  allowedFileTypes,
  renderButton,
  isOpen,
  onClose,
  usePrivateUpload = false,
}: FileUploadModalWithButtonProps | FileUploadModalWithoutButtonProps) {
  const { t } = useTranslation("ui-components-classroom\\FilestackUpload");
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = React.useCallback(() => {
    if (onClose) {
      onClose();
      return;
    }
    setIsModalOpen(false);
  }, [onClose]);

  const handleFilesUploaded = React.useCallback(
    (files: OsUppyFile[]) => {
      if (onFilesUploaded) {
        return onFilesUploaded(files);
      }
      if (!onUploaded) {
        return;
      }
      const converted = files.map(file =>
        reformatUppyFile({
          uppyFile: file,
          source: file.source ?? "uppy",
        }),
      );
      return onUploaded(converted);
    },
    [onFilesUploaded, onUploaded],
  );

  const uppyHeight = 550;
  const uppyWidth = 750;

  return (
    <>
      {renderButton && renderButton(handleOpenModal)}
      <BasicModal
        open={isOpen !== undefined ? isOpen : isModalOpen}
        onClose={handleCloseModal}
        hasCloseButton
        closeButtonArialLabel={t("Close upload modal")}
        modalContentProps={{
          sx: {
            padding: 0,
          },
        }}
        modalCloseButtonProps={{
          sx: (theme: Theme) => ({
            zIndex: theme.zIndex.modal + 1,
          }),
        }}
      >
        <Box sx={{ marginTop: "35px" }}>
          <UppyComponent
            height={uppyHeight}
            width={uppyWidth}
            maxNumberOfFiles={maxFiles}
            maxFileSize={maxSize}
            allowedFileTypes={allowedFileTypes}
            onFileAdded={onFileAdded}
            onFilesUploaded={handleFilesUploaded}
            cropRatio={cropRatio}
            allowedSources={uppyAllowedSources as UppyAllowedSources}
            usePrivateUpload={usePrivateUpload}
            onSuccess={handleCloseModal}
          />
        </Box>
      </BasicModal>
      <GlobalFilestackTweaksStyle />
    </>
  );
}

const GlobalFilestackTweaksStyle = () => (
  <Global
    styles={css`
      .fsp-picker--inline {
        z-index: 1;
      }
      /*
        From https://github.com/filestack/filestack-js/issues/335#issuecomment-657988398
        This style definition positions the "less than X MB" error at the top of the
        screen, rather than the top of the page, which may be out-of-view
      */
      .fsp-notifications__container {
        position: fixed !important;
      }
    `}
  />
);

export { FileUploadModal };
